<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, parseISO } from 'date-fns';
import DetailRow from './DetailRow.vue';

const props = defineProps({
  details: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const KEY = 'CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.CAR';

const capitalize = value =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

const formatAt = value => {
  if (!value) return '';
  // Naive wall-clock strings have no timezone; parseISO treats them as local,
  // preserving the original digits without any UTC shift.
  try {
    return format(parseISO(value), 'dd MMM yyyy, HH:mm');
  } catch {
    return value;
  }
};

// The manual form seeds `vehicle: {}`, so an object alone is not enough:
// without at least one filled field the card would be an empty box.
const vehicle = computed(() => {
  const value = props.details.vehicle;
  return value && Object.values(value).some(Boolean) ? value : null;
});

const vehicleSpecs = computed(() => {
  const v = vehicle.value;
  if (!v) return [];
  const specs = [];
  if (v.seats) specs.push({ icon: 'i-lucide-users', label: v.seats });
  if (v.bags) specs.push({ icon: 'i-lucide-briefcase', label: v.bags });
  if (v.doors) specs.push({ icon: 'i-lucide-door-open', label: v.doors });
  if (v.transmission) {
    specs.push({
      icon: 'i-lucide-settings-2',
      label: capitalize(v.transmission.toLowerCase()),
    });
  }
  if (v.fuel) specs.push({ icon: 'i-lucide-fuel', label: v.fuel });
  if (v.airCon) {
    specs.push({
      icon: 'i-lucide-snowflake',
      label: t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.AC'),
    });
  }
  if (v.power?.hp) {
    specs.push({
      icon: 'i-lucide-zap',
      label: t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.POWER_HP', { hp: v.power.hp }),
    });
  }
  return specs;
});

const pickup = computed(() => props.details.pickup || null);
const dropoff = computed(() => props.details.dropoff || null);
const durationDays = computed(() => props.details.duration?.days || null);
const provider = computed(() => props.details.provider || '');
const ratePlan = computed(() => props.details.ratePlan || '');

const driver = computed(() => props.details.driver || null);

const driverName = computed(() => {
  if (!driver.value) return '';
  const first = driver.value.firstName || '';
  const last = driver.value.lastName || '';
  if (first && last && first === last) return first;
  return [first, last].filter(Boolean).join(' ');
});

const flightNumber = computed(() => props.details.flightNumber || '');

const hasRental = computed(
  () =>
    pickup.value ||
    dropoff.value ||
    durationDays.value ||
    provider.value ||
    flightNumber.value
);

const hasDriver = computed(
  () =>
    driverName.value ||
    driver.value?.email ||
    driver.value?.countryOfResidence ||
    driver.value?.age
);

const insurances = computed(() =>
  Array.isArray(props.details.insurances) ? props.details.insurances : []
);

const charges = computed(() =>
  Array.isArray(props.details.charges) ? props.details.charges : []
);

const taxes = computed(() =>
  (Array.isArray(props.details.taxes) ? props.details.taxes : []).filter(
    tax => tax.rate
  )
);

const hasPricing = computed(
  () => insurances.value.length || charges.value.length || taxes.value.length
);

const hasCarDetails = computed(
  () =>
    vehicle.value ||
    hasRental.value ||
    hasDriver.value ||
    hasPricing.value ||
    props.details.notes
);
</script>

<template>
  <div v-if="hasCarDetails" class="flex flex-col gap-5">
    <!-- Vehicle -->
    <div v-if="vehicle" class="space-y-2">
      <p class="text-xs font-medium text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.VEHICLE') }}
      </p>
      <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 space-y-3">
        <div class="flex items-start gap-3">
          <img
            v-if="vehicle.imageUrl"
            :src="vehicle.imageUrl"
            :alt="vehicle.sampleModel"
            class="size-12 shrink-0 rounded-lg object-contain"
          />
          <div class="min-w-0 flex-1 space-y-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{ vehicle.sampleModel }}
            </p>
            <span
              v-if="vehicle.category"
              class="inline-block rounded-md bg-n-alpha-2 px-2 py-0.5 font-mono text-xs text-n-slate-11"
            >
              {{ vehicle.category }}
            </span>
          </div>
        </div>
        <div
          v-if="vehicleSpecs.length"
          class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-11"
        >
          <span
            v-for="(spec, idx) in vehicleSpecs"
            :key="idx"
            class="inline-flex items-center gap-1"
          >
            <span :class="spec.icon" class="size-3.5" />{{ spec.label }}
          </span>
        </div>
      </div>
    </div>

    <!-- Rental -->
    <div v-if="hasRental" class="space-y-2">
      <p class="text-xs font-medium text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.RENTAL') }}
      </p>
      <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 space-y-2">
        <div
          v-if="pickup"
          class="flex items-baseline justify-between gap-4 text-sm"
        >
          <span class="text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.PICKUP') }}
          </span>
          <span class="text-right text-n-slate-12">
            {{ pickup.locationName || pickup.locationCode }} ·
            {{ formatAt(pickup.at) }}
          </span>
        </div>
        <div
          v-if="dropoff"
          class="flex items-baseline justify-between gap-4 text-sm"
        >
          <span class="text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.DROPOFF') }}
          </span>
          <span class="text-right text-n-slate-12">
            {{ dropoff.locationName || dropoff.locationCode }} ·
            {{ formatAt(dropoff.at) }}
          </span>
        </div>
        <div
          v-if="durationDays"
          class="flex items-baseline justify-between gap-4 text-sm"
        >
          <span class="text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.DURATION') }}
          </span>
          <span class="text-right text-n-slate-12">
            {{
              t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.DURATION_DAYS', {
                count: durationDays,
              })
            }}
          </span>
        </div>
        <div
          v-if="provider"
          class="flex items-baseline justify-between gap-4 text-sm"
        >
          <span class="text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.PROVIDER') }}
          </span>
          <span
            class="flex items-center gap-1.5 text-right text-n-slate-12 capitalize"
          >
            {{ provider }}
            <span
              v-if="ratePlan"
              class="rounded-md bg-n-alpha-2 px-1.5 py-0.5 font-mono text-xs normal-case text-n-slate-11"
            >
              {{ ratePlan }}
            </span>
          </span>
        </div>
        <DetailRow :label="t(`${KEY}.FLIGHT_NUMBER`)" :value="flightNumber" />
        <p v-if="pickup || dropoff" class="text-xs text-n-slate-10">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.LOCAL_TIME_NOTE') }}
        </p>
      </div>
    </div>

    <!-- Driver -->
    <div v-if="hasDriver" class="space-y-2">
      <p class="text-xs font-medium text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.DRIVER') }}
      </p>
      <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 space-y-2">
        <div
          v-if="driverName"
          class="flex items-baseline justify-between gap-4 text-sm"
        >
          <span class="text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.DRIVER_NAME') }}
          </span>
          <span class="text-right text-n-slate-12">{{ driverName }}</span>
        </div>
        <div
          v-if="driver?.email"
          class="flex items-baseline justify-between gap-4 text-sm"
        >
          <span class="text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.EMAIL') }}
          </span>
          <span class="text-right text-n-slate-12">{{ driver.email }}</span>
        </div>
        <div
          v-if="driver?.countryOfResidence"
          class="flex items-baseline justify-between gap-4 text-sm"
        >
          <span class="text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.COUNTRY') }}
          </span>
          <span class="text-right text-n-slate-12 capitalize">
            {{ driver.countryOfResidence }}
          </span>
        </div>
        <DetailRow :label="t(`${KEY}.AGE`)" :value="driver?.age" />
      </div>
    </div>

    <!-- Pricing -->
    <div v-if="hasPricing" class="space-y-2">
      <p class="text-xs font-medium text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.PRICING') }}
      </p>
      <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 space-y-3">
        <div v-if="insurances.length" class="space-y-2">
          <p class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.INSURANCES') }}
          </p>
          <div
            v-for="(insurance, idx) in insurances"
            :key="`ins-${idx}`"
            class="flex items-baseline justify-between gap-4 text-sm"
          >
            <span class="text-n-slate-11">{{ insurance.name }}</span>
            <span
              v-if="insurance.type === 'INCLUDED'"
              class="shrink-0 rounded-full bg-n-green-3 px-2 py-0.5 text-xs font-medium text-n-green-11"
            >
              {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.INCLUDED') }}
            </span>
            <span
              v-else-if="insurance.price?.amount > 0"
              class="text-right text-n-slate-12"
            >
              {{ insurance.price.amount }} {{ insurance.price.currency }}
            </span>
            <span v-else class="text-right text-n-slate-11 capitalize">
              {{ insurance.type?.toLowerCase() }}
            </span>
          </div>
        </div>

        <div v-if="charges.length" class="space-y-2">
          <p class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.CHARGES') }}
          </p>
          <div
            v-for="(charge, idx) in charges"
            :key="`chg-${idx}`"
            class="flex items-baseline justify-between gap-4 text-sm"
          >
            <span class="text-n-slate-11">{{ charge.description }}</span>
            <span class="text-right text-n-slate-12">
              {{ charge.amount?.amount }} {{ charge.amount?.currency }}
            </span>
          </div>
        </div>

        <div v-if="taxes.length" class="space-y-2">
          <p class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.TAXES') }}
          </p>
          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="(tax, idx) in taxes"
              :key="`tax-${idx}`"
              class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
            >
              {{ tax.code }} {{ tax.rate }}%
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Notes -->
    <div
      v-if="details.notes"
      class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
    >
      <DetailRow
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.NOTES')"
        :value="details.notes"
      />
    </div>
  </div>
</template>
